import { splitInlineExpression } from "@/components/input/utils/splitInlineExpression";
import { copyPasteKey } from "@/components/tiptap/copy-paste/copyPasteKey";
import { hydratePastedContent } from "@/components/tiptap/copy-paste/hydratePastedContent";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorState } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

export function duplicateCurrentTiptapSelection(
  doc: DocumentModel,
  view: EditorView,
  { insertAt, uploadScope }: { insertAt?: number; uploadScope?: UploadScope } = {},
): boolean {
  const { tiptap } = doc.editor;
  if (!tiptap) return false;

  const pluginState = copyPasteKey.getState(view.state);

  // Get current selection slice (like a real copy operation would).
  let slice = view.state.selection.content();
  type SliceJsonNode = { type?: string; content?: SliceJsonNode[] } | null;
  const sliceJson = slice.content.toJSON() as SliceJsonNode[];
  const hasType = (nodes: SliceJsonNode[], type: string): boolean =>
    nodes.some((n) => {
      if (!n || typeof n !== "object") return false;
      if (n.type === type) return true;
      return Array.isArray(n.content) && n.content.length ? hasType(n.content, type) : false;
    });
  const containsInlineExpression = hasType(sliceJson, "inlineExpression");
  const containsText = hasType(sliceJson, "text");

  // Apply CopyPaste's transformCopied (and any other registered transforms).
  view.someProp("transformCopied", (f) => {
    slice = f(slice, view);
  });

  // Apply transformPasted which populates copyPaste plugin state (parsedNodes) and rewrites attrs.
  view.someProp("transformPasted", (f) => {
    slice = f(slice, view, false);
  });

  // Set insertion caret to the requested position (default: caret/head).
  let targetPos = insertAt ?? view.state.selection.head;

  // If selection ends in math AND the selection includes both text and math,
  // split inlineExpression at selection end to keep insertion inside math.
  if (containsInlineExpression && containsText) {
    const inlineSelection = doc.selection.range;
    const direction = doc.selection.direction ?? "forward";
    const inlineSelectionNode = inlineSelection?.node ?? doc.cursor.node;
    const inlineSelectionIndex =
      inlineSelection && direction === "backward"
        ? inlineSelection.anchor
        : (inlineSelection?.head ?? doc.cursor.index);

    if (
      inlineSelectionNode?.parent?.is(WrapperNodeModel) &&
      inlineSelectionIndex !== undefined &&
      inlineSelectionIndex < inlineSelectionNode.nodes.length
    ) {
      doc.cursor.moveTo(inlineSelectionNode, inlineSelectionIndex, { resetSelection: false });
      const insertionPos = splitInlineExpression(doc, "");
      if (typeof insertionPos === "number") {
        targetPos = insertionPos;
      }
    }
  }

  if (typeof targetPos === "number") {
    tiptap.commands.setTextSelection({ from: targetPos, to: targetPos });
    tiptap.commands.focus(targetPos);
  }

  // Hydrate DocumentModel nodes that correspond to inlineExpression/graph/etc.
  // In real paste, ProseMirror will do the actual insertion when this returns false.
  // Here we insert manually when not partial.
  const insertFrom = targetPos;
  const sliceSize = slice.size;

  doc.selection.resetSelection();
  const isPartial = hydratePastedContent(pluginState, doc, null, view, "duplicated", uploadScope);
  if (!isPartial) {
    const tr = view.state.tr.replaceSelection(slice).scrollIntoView();
    view.dispatch(tr);
  }

  // Select all new duplicated content so it can be duplicated again
  // Use requestAnimationFrame to ensure the DOM is updated
  requestAnimationFrame(() => {
    const insertTo = insertFrom + sliceSize;

    // First select any math nodes within the duplicated range
    // Do this BEFORE setTextSelection so it doesn't get cleared by ExpressionSelection.onSelectionUpdate
    selectWrapperNodesInRange(doc, tiptap.state, insertFrom, insertTo);

    // Then set the TipTap text selection
    tiptap.commands.setTextSelection({ from: insertFrom, to: insertTo });
    tiptap.commands.focus();
  });

  return true;
}

/**
 * Select all wrapper nodes (math expressions) within a TipTap range.
 * This ensures math nodes are properly selected in doc.selection.
 */
function selectWrapperNodesInRange(
  doc: DocumentModel,
  state: EditorState,
  from: number,
  to: number,
) {
  state.doc.nodesBetween(from, to, (node) => {
    if (nodeIsExpression(node)) {
      const id = String(node.attrs.nodeId ?? "");
      if (id) {
        const wrapperNode = doc.nodes.get(id);
        if (wrapperNode?.is(WrapperNodeModel) && wrapperNode.inlineNode) {
          // Use setInlineNodeRangeBetween directly instead of selectWrapperNode
          // because selectWrapperNode checks cursor position which may prevent selection
          const { inlineNode } = wrapperNode;
          doc.selection.setInlineNodeRangeBetween(
            inlineNode,
            0,
            inlineNode,
            inlineNode.nodes.length,
          );
          return false;
        }
      }
    }
    return true;
  });
}
